import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import MessagesObject from '../interfaces/MessagesObject';

export interface IRFIConversation {
  id: string;
  recipient_full_name: string;
  recipient_email: string;
  conversation_subject: string;
  messages: MessagesObject[];
  case_manager_id?: number;
  recipient_id?: number;
  latest_message?: string;
  unread_messages_count?: number;
  status: number;
}

export enum Sender {
  CASE_MANAGER = 'case manager',
  RECIPIENT = 'recipient',
}

export type RFIComposeMessageOption = Pick<IRFIConversation, 'id' | 'recipient_full_name' | 'recipient_email' | 'conversation_subject' | 'status'>;
export interface LoadingStates {
  [conversationId: string]: boolean;
}

export interface UnreadMessages {
  [conversationId: string]: number;
}

interface IRFIMessageState {
  composeOptions: RFIComposeMessageOption[];
  rfiConversations: IRFIConversation[];
  activeRFIConversation: IRFIConversation['id'];
  loadingStates: LoadingStates;
  unreadMessages: UnreadMessages;
  activeTab?: string;
}
const initialState: IRFIMessageState = {
  composeOptions: [],
  rfiConversations: [],
  activeRFIConversation: '',
  loadingStates: {},
  unreadMessages: {},
  activeTab: '',
};

const rfiMessageSlice = createSlice({
  name: 'rfiMessage',
  initialState,
  reducers: {
    setComposeOptions: (
      state,
      action: PayloadAction<RFIComposeMessageOption[]>,
    ) => {
      state.composeOptions = action.payload;
    },
    setRFIConversations: (state, action: PayloadAction<IRFIConversation[]>) => {
      state.rfiConversations = action.payload;
    },
    setActiveRFIConversation: (state, action: PayloadAction<string>) => {
      state.activeRFIConversation = action.payload;
    },
    createRFIConversation: (state, action: PayloadAction<IRFIConversation>) => {
      state.rfiConversations.push(action.payload);
    },
    createRFIConversationMessage: (
      state,
      action: PayloadAction<{
        conversationID: IRFIConversation['id'];
        message: MessagesObject;
      }>,
    ) => {
      const conversation = state.rfiConversations.find(
        (rfiConversation) =>
          rfiConversation.id === action.payload.conversationID,
      );
      //if message id already exists, do not add it
      if (
        conversation && conversation.messages &&
        !conversation.messages.find(
          (message) => message.messageId === action.payload.message.messageId,
        )
      ) {
        conversation.messages.push(action.payload.message);
      }
    },
    updateRFIConversationMessage: (
      state,
      action: PayloadAction<{
        conversationID: IRFIConversation['id'];
        messages: MessagesObject[];
      }>,
    ) => {
      state.rfiConversations = state.rfiConversations.map((rfiConversation) => {
        if (rfiConversation.id === action.payload.conversationID) {
          return {
            ...rfiConversation,
            messages: action.payload.messages,
          };
        }
        return rfiConversation;
      });
    },
    setLoadingState: (state, action: PayloadAction<{ conversationId: string; isLoading: boolean }>) => {
      state.loadingStates[action.payload.conversationId] = action.payload.isLoading;
    },
    setUnreadMessages: (state, action:PayloadAction<{ conversationId: string; number: number }>) => {
      state.unreadMessages[action.payload.conversationId] = action.payload.number;
    },
    setTabState: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setComposeOptions,
  setRFIConversations,
  setActiveRFIConversation,
  createRFIConversation,
  createRFIConversationMessage,
  updateRFIConversationMessage,
  setLoadingState,
  setUnreadMessages,
  setTabState,
} = rfiMessageSlice.actions;

export default rfiMessageSlice.reducer;
